import { default as ReactEcharts } from "echarts-for-react";
import { t } from "i18next";
import { DateTime } from "luxon";
import ReactDomServer from "react-dom/server";

import _ from "lodash";
import { Equipment, Event, FShift, UsageDataset } from "../../../../../types/types";
import { hslColor, rgbColor } from "../../../../../enums/colors";
import { PersonUsageRow, PersonUsageRowInteraction, SummaryPersonUsageRow, SummaryPersonUsageRowInteraction } from "../../../../../stores/TabsStore";
import { useEffect, useState } from "react";


interface GrpahData {
  name: string;
  type: string;
  data: number[];
  color: string;
  stack?: string;
  markLine?: any;
}

interface ChartData {
  totalWorked: number;
  totalWorkedPercent: number;
  totalStopped: number;
  totalTurnedOff: number;
  totalBroken: number;
  totalRepair: number;
  totalMaintenance: number;
  totalOther: number;
  chartData: {
    days: string[],
    worked: number[],
    stopped: number[],
    broken: number[],
    repair: number[],
    maintenance: number[],
    turned_off: number[],
    other: number[]
    workedPercents: number[],
    stoppedPercents: number[],
    repairPercents: number[],
    brokenPercents: number[],
    maintenancePercents: number[], 
    otherPercents: number[],
    turned_offPercents: number[]
  }
}



export const LoadBarChart = ({data, by}:{data: SummaryPersonUsageRow, by: "days" | "equipment" | string}) => {
  let days = data.data.map(d => d.dateShift.start_day.toSQLDate())
  const [loading, setLoading] = useState(true)
  const [graphData, setGraphData] = useState<GrpahData[]>([])

  useEffect(() => {
    setLoading(true)
  }, [by])

  function getDataForDays(interaction: SummaryPersonUsageRowInteraction, state: "worked" | "stopped" | "turned_off") {
    let chartData: number[] = []
    days.map(day => {
      let dayD = data.data.filter(d => d.dateShift.start_day.toSQLDate() === day).map(d => d.interactions.filter(i => i.equipment.id === interaction.equipment.id)).flat()
      if(dayD.length > 0) {
        if(state === "worked") {chartData.push(_.sum(dayD.map(e => (e.availability.worked.percent + e.unavailability.worked.percent) * 100)))}
        if(state === "stopped") {chartData.push(_.sum(dayD.map(e => (e.availability.stopped.percent + e.unavailability.stopped.percent) * 100)))}
        if(state === "turned_off") {chartData.push(_.sum(dayD.map(e => (e.availability.off.percent + e.unavailability.off.percent) * 100)))}
      } else {
        chartData.push(0)
      }
    })
    return chartData
  }

  useEffect(() => {
    if(!loading) { return }
    let chartData = []
    if(by === "days") {
      chartData.push({
        name: "manual",
        type: "bar",
        stack: "worked",
        data: data.data.map(d => d.manual.worked.percent * 100),
        color: rgbColor.worked,
      })
      chartData.push({
        name: "manual",
        type: "bar",
        stack: "stopped",
        data: data.data.map(d => d.manual.stopped.percent * 100),
        color: rgbColor.stopped,
      })
      chartData.push({
        name: "manual",
        type: "bar",
        stack: "turned_off",
        data: data.data.map(d => d.manual.off.percent * 100),
        color: rgbColor.turned_off,
      })
  
      chartData.push({
        name: "maintenance",
        type: "bar",
        stack: "worked",
        data: data.data.map(d => d.maintenance.worked.percent * 100),
        color: rgbColor.worked,
      })
      chartData.push({
        name: "maintenance",
        type: "bar",
        stack: "stopped",
        data: data.data.map(d => d.maintenance.stopped.percent * 100),
        color: rgbColor.stopped,
      })
      chartData.push({
        name: "maintenance",
        type: "bar",
        stack: "turned_off",
        data: data.data.map(d => d.maintenance.off.percent * 100),
        color: rgbColor.turned_off,
      })
  
      chartData.push({
        name: "repair",
        type: "bar",
        stack: "worked",
        data: data.data.map(d => d.repair.worked.percent * 100),
        color: rgbColor.worked,
      })
      chartData.push({
        name: "repair",
        type: "bar",
        stack: "stopped",
        data: data.data.map(d => d.repair.stopped.percent * 100),
        color: rgbColor.stopped,
      })
      chartData.push({
        name: "repair",
        type: "bar",
        stack: "turned_off",
        data: data.data.map(d => d.repair.off.percent * 100),
        color: rgbColor.turned_off,
      })

      chartData.push({
        name: "service",
        type: "bar",
        stack: "worked",
        data: data.data.map(d => d.service.worked.percent * 100),
        color: rgbColor.worked,
      })
      chartData.push({
        name: "service",
        type: "bar",
        stack: "stopped",
        data: data.data.map(d => d.service.stopped.percent * 100),
        color: rgbColor.stopped,
      })
      chartData.push({
        name: "service",
        type: "bar",
        stack: "turned_off",
        data: data.data.map(d => d.service.off.percent * 100),
        color: rgbColor.turned_off,
      })
    } else {
      data.interactions.map(eq => {
        chartData.push({
          name: `worked`,
          type: "bar",
          stack: String(eq.equipment.id),
          data: getDataForDays(eq, "worked"),
          color: rgbColor.worked,
        })
        chartData.push({
          name: `stopped`,
          type: "bar",
          stack: String(eq.equipment.id),
          data: getDataForDays(eq, "stopped"),
          color: rgbColor.stopped,
        })
        chartData.push({
          name: `turned_off`,
          type: "bar",
          stack: String(eq.equipment.id),
          data: getDataForDays(eq, "turned_off"),
          color: rgbColor.turned_off,
        })
      })
    }
    setGraphData(chartData)

    setLoading(false)
  }, [loading])

  const getOptions = () => ({
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: function (params: {name: any; seriesName: string; value: (number | string | undefined)[] }[]) {
        return ReactDomServer.renderToStaticMarkup(<Tooltip params={params} />);
      }
    },
    legend: {
      icon: "circle",
      bottom: 0,
      formatter: function (name: string) {
        return t(name);
      }
    },
    grid: {
      left: "6px",
      right: "0%",
      bottom: "30px",
      top: "10px",
      containLabel: true,
    },
    xAxis: [
      {
        type: "category",
        data: days,
        axisLabel: {
          formatter: function (value: string) {
            return DateTime.fromSQL(value).toFormat("dd");
          },
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        splitLine: {
          lineStyle: {
            color: "rgba(128,128,128,0.1)",
          },
        },
        position: "left",
        axisLabel: {
          max: 100,
          formatter: function (value: string) {
            return `${value} %`;
          },
        },
      },
    ],
    series: graphData,
  });

  return (
    
    <ReactEcharts 
      showLoading={loading} 
      loadingOption={{
        text: '',
        color: hslColor.primary,
        // textColor: store.theme === "dark" ? hslColor.lightBorder : hslColor.darkBorder,
        // maskColor: store.theme === "dark" ? hexColor.borderForeground : hexColor.border,
        zlevel: 0,
        // Font size. Available since `v4.8.0`.
        fontSize: 12,
        // Show an animated "spinner" or not. Available since `v4.8.0`.
        showSpinner: true,
        // Radius of the "spinner". Available since `v4.8.0`.
        spinnerRadius: 8,
        // Line width of the "spinner". Available since `v4.8.0`.
        lineWidth: 2,
        // Font thick weight. Available since `v5.0.1`.
        fontWeight: 'normal',
        // Font style. Available since `v5.0.1`.
        fontStyle: 'normal',
        // Font family. Available since `v5.0.1`.
        fontFamily: 'sans-serif'
      }}
      option={getOptions()} 
      style={{ height: 200, width: "100%" }} 
      opts={{ renderer: "canvas" }} 
    />
  )
}

const Tooltip = ({ params }: { params: { name: any; seriesName: string; value: (number | string | undefined)[] }[] }) => {
  let date = DateTime.fromSQL(params[0].name!)
  return (
    <div className="relative bg-background text-xs">
      <div className="font-bold uppercase pb-3">{date.weekdayShort} {date.toFormat("dd.MM.yyyy")}</div>
      <div className="flex flex-col gap-1">
        {params.map(item => (
          <div key={item.name} className="flex w-48 justify-between">
            <div className="flex truncate">{t(`${item.seriesName}`)}</div>
            <div className="font-bold">{Number(item.value).toFixed(2)}%</div>
          </div>
        ))}
      </div>

    </div>
  );
};